import axios from 'axios';
import { queueRequest, processQueue, isOnline } from './offlineQueue';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://api.mikepestpatrol.com/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// ─── Challenge state management ───────────────────────────────────────────────
// These are set by AuthContext after mount so the interceptor can communicate
// back to the React component tree without circular imports.

let _setChallengeActive = null;

export function registerChallengeHandler(setChallengeActive) {
  _setChallengeActive = setChallengeActive;
}

// ─── Helper: determine current user role from localStorage token ──────────────
function getCurrentUserRole() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.role || null;
  } catch {
    return null;
  }
}

// ─── Request interceptor ──────────────────────────────────────────────────────

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Response interceptor ─────────────────────────────────────────────────────

apiClient.interceptors.response.use(
  (response) => {
    // Return just the data for convenience
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    // Check if request failed due to being offline -- technician-only queueing
    if (!isOnline() && !originalRequest._queued) {
      const role = getCurrentUserRole();
      if (role === 'technician') {
        try {
          const method = originalRequest.method.toUpperCase();
          const url = originalRequest.url.replace(originalRequest.baseURL, '');
          const data = originalRequest.data ? JSON.parse(originalRequest.data) : null;

          await queueRequest(method, url, data);
          originalRequest._queued = true;

          return Promise.resolve({
            success: false,
            queued: true,
            message: 'Request queued for later - you are currently offline'
          });
        } catch (queueError) {
          console.error('Failed to queue request:', queueError);
        }
      }
    }

    // Handle 403 with turnstile_challenge -- show challenge overlay, NOT redirect
    if (
      error.response?.status === 403 &&
      error.response?.data?.turnstile_challenge
    ) {
      if (_setChallengeActive) {
        _setChallengeActive(true);
      }
      return Promise.reject(error);
    }

    // Handle must_change_password 403
    if (error.response?.status === 403 && error.response?.data?.must_change_password) {
      window.location.href = '/change-password';
      return Promise.reject(error);
    }

    // Handle 429 rate limit
    if (error.response?.status === 429) {
      return Promise.reject({
        success: false,
        message: 'Too many requests. Please wait a moment and try again.',
        rateLimited: true,
      });
    }

    // Handle authentication errors
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Handle other errors
    if (error.response) {
      return Promise.reject(error.response.data || error.response);
    } else if (error.request) {
      return Promise.reject({
        success: false,
        message: 'No response from server'
      });
    } else {
      return Promise.reject({
        success: false,
        message: error.message
      });
    }
  }
);

// ─── Online event: process queue ──────────────────────────────────────────────

window.addEventListener('online', async () => {
  const role = getCurrentUserRole();
  if (role !== 'technician') return;

  console.log('[API] Connection restored, processing queued requests...');
  try {
    const results = await processQueue(apiClient);
    console.log('[API] Queue processing complete:', results);

    if (results.challengePaused) {
      console.log('[API] Queue paused for Turnstile challenge');
      if (_setChallengeActive) {
        _setChallengeActive(true);
      }
      return;
    }

    if (results.processed > 0) {
      console.log(`${results.processed} queued request(s) processed successfully`);
    }
    if (results.failed > 0) {
      console.warn(`${results.failed} queued request(s) failed to process`);
    }
  } catch (error) {
    console.error('[API] Error processing queue:', error);
  }
});

// ============================================================================
// Inventory / Chemical API functions
// ============================================================================

export const getChemicals = () => apiClient.get('/inventory/chemicals');

export const getChemical = (id) => apiClient.get(`/inventory/chemicals/${id}`);

export const createChemical = (data) => apiClient.post('/inventory/chemicals', data);

export const updateChemical = (id, data) => apiClient.put(`/inventory/chemicals/${id}`, data);

export const getRecipesForServiceType = (serviceTypeId) =>
  apiClient.get(`/inventory/recipes/${serviceTypeId}`);

export const createOrUpdateRecipe = (data) => apiClient.post('/inventory/recipes', data);

export const deleteRecipe = (id) => apiClient.delete(`/inventory/recipes/${id}`);

export const getInventoryTransactions = (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.chemical_id) params.append('chemical_id', filters.chemical_id);
  if (filters.service_id) params.append('service_id', filters.service_id);
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);
  if (filters.transaction_type) params.append('transaction_type', filters.transaction_type);
  const query = params.toString();
  return apiClient.get(`/inventory/transactions${query ? `?${query}` : ''}`);
};

export const createInventoryTransaction = (data) =>
  apiClient.post('/inventory/transactions', data);

// ============================================================================
// Service API functions
// ============================================================================

export const getChemicalEstimates = (serviceId) =>
  apiClient.get(`/services/${serviceId}/chemical-estimates`);

// ============================================================================
// Reference Data API functions
// ============================================================================

export const getReferenceData = () => apiClient.get('/reference/all');

export const getServiceTypes = () => apiClient.get('/reference/types');

export default apiClient;
